import {
  AI_QUOTA_PAYWALL_PRICE_PER_SEAT_ANNUAL,
  AI_QUOTA_PAYWALL_PRICE_PER_SEAT_MONTHLY,
} from "@superblocksteam/shared";
import { Checkbox, Modal } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ReactComponent as CrossIcon } from "assets/icons/common/cross.svg";
import { selectOnlyOrganization } from "store/slices/organizations";
import { useUpdateSubscriptionMutation } from "store/slices/reduxApi/billing";
import {
  useAssignBuilderSeatMutation,
  useListBuilderSeatsQuery,
} from "store/slices/reduxApi/builderSeats";
import { useListUsersQuery } from "store/slices/reduxApi/users";
import type { Billing, OrganizationUserDto } from "@superblocksteam/shared";

type AssignBuilderSeatsModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** When true, show banner indicating current user does not have an AI Builder seat (when opened from paywall flow) */
  showNoSeatBanner?: boolean;
};

function getAssignableUsers(
  orgUsers: OrganizationUserDto[] | undefined,
  builderUserIds: Set<string>,
  builderEmails: Set<string>,
): OrganizationUserDto[] {
  if (!orgUsers) return [];
  return orgUsers.filter((u) => {
    const hasSeat =
      builderUserIds.has(u.id) ||
      builderEmails.has(u.email.trim().toLowerCase());
    return !hasSeat;
  });
}

export function AssignBuilderSeatsModal({
  open,
  onClose,
  onSuccess,
  showNoSeatBanner = false,
}: AssignBuilderSeatsModalProps) {
  const org = useSelector(selectOnlyOrganization);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [assignErrors, setAssignErrors] = useState<string[]>([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const { data: builderSeatsData, refetch: refetchBuilderSeats } =
    useListBuilderSeatsQuery(undefined, {
      skip: !open,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    });
  const { data: orgUsers } = useListUsersQuery(
    { organizationId: org?.id ?? "" },
    { skip: !open || !org?.id },
  );

  const builderUserIds = useMemo(
    () => new Set(builderSeatsData?.builders?.map((b) => b.userId) ?? []),
    [builderSeatsData],
  );
  const builderEmails = useMemo(
    () =>
      new Set(
        builderSeatsData?.builders?.map((b) => b.email.trim().toLowerCase()) ??
          [],
      ),
    [builderSeatsData],
  );
  const assignableUsers = useMemo(
    () => getAssignableUsers(orgUsers, builderUserIds, builderEmails),
    [orgUsers, builderUserIds, builderEmails],
  );
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return assignableUsers;
    const q = search.trim().toLowerCase();
    return assignableUsers.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q),
    );
  }, [assignableUsers, search]);

  const [assign, { isLoading: isAssigning }] = useAssignBuilderSeatMutation();
  const [updateSubscription, { isLoading: isUpdatingSubscription }] =
    useUpdateSubscriptionMutation();

  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set(
        Array.from(prev).filter((userId) =>
          assignableUsers.some((user) => user.id === userId),
        ),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [assignableUsers]);

  // Refetch after a short delay when modal opens to allow Stripe webhooks to settle
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      void refetchBuilderSeats();
    }, 1000);
    return () => clearTimeout(timer);
  }, [open, refetchBuilderSeats]);

  const handleToggle = useCallback((userId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(userId);
      else next.delete(userId);
      return next;
    });
    setAssignErrors([]);
  }, []);

  const handleClose = useCallback(() => {
    setSearch("");
    setSelectedIds(new Set());
    setAssignErrors([]);
    setConfirmModalOpen(false);
    onClose();
  }, [onClose]);

  const selectedCount = selectedIds.size;
  const assignedSeatCount = builderSeatsData?.builders.length ?? 0;
  const currentTotalSeats =
    builderSeatsData?.totalPurchasedSeats ?? assignedSeatCount;
  const availableSeatsLeft = Math.max(currentTotalSeats - assignedSeatCount, 0);
  const additionalSeatsToPurchase = Math.max(
    selectedCount - availableSeatsLeft,
    0,
  );
  const newTotalSeats = currentTotalSeats + additionalSeatsToPurchase;
  const billing = org?.billing as Billing | undefined;
  const billingInterval =
    (billing as Billing & { billingInterval?: string; interval?: string })
      ?.billingInterval === "annual" ||
    (billing as Billing & { billingInterval?: string; interval?: string })
      ?.interval === "annual"
      ? "annual"
      : "monthly";
  // Annual plan: $80/seat/month. Monthly plan: $100/seat/month.
  const perSeatPrice =
    billingInterval === "annual"
      ? AI_QUOTA_PAYWALL_PRICE_PER_SEAT_ANNUAL
      : AI_QUOTA_PAYWALL_PRICE_PER_SEAT_MONTHLY;
  const totalAddedPrice = additionalSeatsToPurchase * perSeatPrice;
  const priceSuffix = "/month";

  const handleSubmit = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setAssignErrors([]);
    const errors: string[] = [];
    if (additionalSeatsToPurchase > 0) {
      try {
        await updateSubscription({
          unitType: "builder_seat",
          quantity: newTotalSeats,
          billingInterval,
        }).unwrap();

        for (let attempt = 0; attempt < 8; attempt += 1) {
          const refreshed = await refetchBuilderSeats();
          const refreshedTotalSeats = refreshed.data?.totalPurchasedSeats ?? 0;
          if (refreshedTotalSeats >= newTotalSeats) {
            break;
          }
          await new Promise((resolve) => {
            window.setTimeout(resolve, 750);
          });
        }
      } catch (err: unknown) {
        const data = (err as { data?: { message?: string } })?.data;
        setAssignErrors([
          data?.message ?? "Failed to update license subscription",
        ]);
        return;
      }
    }

    const ids = Array.from(selectedIds);
    for (const userId of ids) {
      try {
        await assign({ userId }).unwrap();
      } catch (err: unknown) {
        const data = (err as { data?: { code?: string; message?: string } })
          ?.data;
        const code = data?.code;
        const msg =
          code === "NO_AVAILABLE_SEATS"
            ? "No available licenses"
            : code === "ALREADY_HAS_SEAT"
              ? "User already has a license"
              : code === "NOT_A_DEVELOPER"
                ? "User is not a Developer"
                : (data?.message ?? "Failed to assign license");
        errors.push(msg);
      }
    }
    if (errors.length > 0) {
      setAssignErrors(errors);
      return;
    }
    setSearch("");
    setSelectedIds(new Set());
    setAssignErrors([]);
    setConfirmModalOpen(false);
    if (onSuccess) {
      onSuccess();
    } else {
      onClose();
    }
  }, [
    selectedIds,
    additionalSeatsToPurchase,
    assign,
    billingInterval,
    newTotalSeats,
    onClose,
    onSuccess,
    refetchBuilderSeats,
    updateSubscription,
  ]);

  const isSubmitting = isAssigning || isUpdatingSubscription;
  const submitLabel =
    selectedCount === 0
      ? "Assign"
      : additionalSeatsToPurchase > 0
        ? `Purchase (${additionalSeatsToPurchase}) & Assign (${selectedCount})`
        : `Assign (${selectedCount})`;

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      width={480}
      footer={null}
      destroyOnClose
      className="tailwind"
      modalRender={(modal) => (
        <div className="overflow-hidden rounded-lg">{modal}</div>
      )}
      closeIcon={
        <span className="flex h-full w-full items-center justify-center text-gray-500">
          <CrossIcon className="h-4 w-4" />
        </span>
      }
      bodyStyle={{ padding: 0 }}
    >
      <div className="flex flex-col">
        <div className="px-5 pb-3 pt-5">
          <h2 className="!m-0 !mb-0 text-lg font-semibold leading-none text-gray-950">
            Assign AI Builder Licenses
          </h2>
          <div className="mt-2 text-sm text-gray-500">
            ({availableSeatsLeft}/{currentTotalSeats} available)
          </div>
        </div>

        {showNoSeatBanner ? (
          <div className="mx-5 mb-3 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <svg
              aria-hidden="true"
              className="mt-0.5 h-4 w-4 shrink-0 text-amber-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 6a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 6Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                clipRule="evenodd"
              />
            </svg>
            <div className="min-w-0 text-sm">
              <p className="m-0 font-semibold text-amber-900">
                You don&apos;t have an AI Builder license
              </p>
              <p className="m-0 mt-0.5 text-amber-700">
                Assign a license to yourself below to start building with Clark
                AI.
              </p>
            </div>
          </div>
        ) : null}

        <div className="px-5 pb-3">
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg
                aria-hidden="true"
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)] outline-none transition focus:border-gray-300 focus:ring-[3px] focus:ring-gray-200/70"
            />
          </div>
        </div>

        {assignErrors.length > 0 && (
          <div className="mx-5 mb-3 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-[13px] leading-snug text-red-700">
            {assignErrors.slice(0, 3).join("; ")}
            {assignErrors.length > 3 && ` (+${assignErrors.length - 3} more)`}
          </div>
        )}

        <div className="max-h-[340px] min-h-0 flex-1 overflow-y-auto px-5 pb-3">
          {assignableUsers.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              No users without a license to assign.
            </p>
          ) : filteredUsers.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              No users found.
            </p>
          ) : (
            <div className="flex flex-col">
              {filteredUsers.map((u) => (
                <label
                  key={u.id}
                  className="flex cursor-pointer items-center gap-3 rounded-md px-1 py-2.5 transition hover:bg-gray-50"
                >
                  <Checkbox
                    checked={selectedIds.has(u.id)}
                    onChange={(e) => handleToggle(u.id, e.target.checked)}
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-950">
                      {u.name ?? u.email}
                    </div>
                    {u.email && (
                      <div className="text-xs text-gray-500">{u.email}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-5 py-4">
          {additionalSeatsToPurchase > 0 && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[13px] leading-snug text-amber-800">
              {additionalSeatsToPurchase}{" "}
              {additionalSeatsToPurchase === 1 ? "license" : "licenses"} x $
              {perSeatPrice}
              {priceSuffix} = ${totalAddedPrice}
              {priceSuffix} added to your plan.
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={selectedCount === 0 || isSubmitting}
              onClick={() => {
                if (additionalSeatsToPurchase > 0) {
                  setConfirmModalOpen(true);
                  return;
                }

                void handleSubmit();
              }}
              className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-black px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting
                ? additionalSeatsToPurchase > 0
                  ? "Purchasing & assigning..."
                  : "Assigning..."
                : submitLabel}
            </button>
          </div>
        </div>
      </div>
      <Modal
        open={confirmModalOpen}
        onCancel={() => setConfirmModalOpen(false)}
        footer={null}
        width={500}
        destroyOnClose
        className="tailwind"
        modalRender={(modal) => (
          <div className="overflow-hidden rounded-lg">{modal}</div>
        )}
      >
        <div className="space-y-4 px-1 py-1">
          <h3 className="m-0 text-lg font-semibold text-gray-950">
            Confirm license assignment
          </h3>
          <p className="m-0 text-sm text-gray-600">
            Please confirm before applying these changes.
          </p>
          <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
            <div className="flex items-center justify-between gap-4">
              <span>Assign licenses</span>
              <span>{selectedCount}</span>
            </div>
            {additionalSeatsToPurchase > 0 ? (
              <div className="flex items-center justify-between gap-4">
                <span>Purchase additional licenses</span>
                <span>
                  {additionalSeatsToPurchase} (
                  {`$${totalAddedPrice}${priceSuffix}`})
                </span>
              </div>
            ) : null}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmModalOpen(false)}
              className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirmModalOpen(false);
                void handleSubmit();
              }}
              disabled={isSubmitting}
              className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-black px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "Applying..." : "Confirm and continue"}
            </button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
}
